import { useState } from "react";
import axios from "axios";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-[#E4DDD0] bg-[#FBF8F3] px-4 py-3.5 text-sm text-[#0A1F18] placeholder:text-[#5C6B64]/60 outline-none transition-colors duration-300 focus:border-[#C87D55]";

export const ContactForm = ({ testidPrefix = "contact-form" }) => {
  const { t } = useLang();
  const f = t.contact.form;
  const [form, setForm] = useState({ name: "", email: "", phone: "", investor_type: f.types[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(f.success);
      setForm({ name: "", email: "", phone: "", investor_type: f.types[0], message: "" });
    } catch {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      data-testid={`${testidPrefix}-form`}
      onSubmit={submit}
      className="border border-[#E4DDD0] bg-white p-8 shadow-xl shadow-[#0A1F18]/5 sm:p-10"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]">{f.name}</label>
          <input
            data-testid={`${testidPrefix}-name-input`}
            required
            value={form.name}
            onChange={set("name")}
            placeholder={f.namePh}
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]">{f.email}</label>
          <input
            data-testid={`${testidPrefix}-email-input`}
            required
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder={f.emailPh}
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]">{f.phone}</label>
          <input
            data-testid={`${testidPrefix}-phone-input`}
            value={form.phone}
            onChange={set("phone")}
            placeholder={f.phonePh}
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]">{f.type}</label>
          <select
            data-testid={`${testidPrefix}-type-select`}
            value={form.investor_type}
            onChange={set("investor_type")}
            className={inputCls}
          >
            {f.types.map((type) => (
              <option key={type} value={type} className="bg-white">
                {type}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]">{f.message}</label>
          <textarea
            data-testid={`${testidPrefix}-message-input`}
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder={f.messagePh}
            className={`${inputCls} resize-none`}
          />
        </div>
      </div>

      <button
        data-testid={`${testidPrefix}-submit-button`}
        type="submit"
        disabled={sending}
        className="group mt-8 flex w-full items-center justify-center gap-3 bg-[#C87D55] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#FBF8F3] transition-colors duration-300 hover:bg-[#0A1F18] disabled:opacity-50"
      >
        {sending ? f.sending : f.submit}
        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};
